import { useEffect, useState } from "react";
import { BellRing, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../i18n";
import { api } from "../../lib/api";
import { currentSubscription, disablePush, enablePush, needsHomeScreen, pushSupported } from "../../lib/push";
import { Switch } from "../ui/switch";
import { btnGhost, btnPrimary, input } from "./Page";

const HOURS = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`);

export const IosGuide = () => {
  const { t } = useI18n();
  return (
    <div className="rounded-xl bg-sky-50 p-3 text-xs text-navy" data-testid="ios-home-screen-guide">
      <p className="flex items-center gap-1.5 font-semibold"><Smartphone className="h-4 w-4" />{t("x.push.iosTitle")}</p>
      <p className="mt-1 text-slate-600">{t("x.push.iosSteps")}</p>
    </div>
  );
};

const Device = ({ cfg, onChange }) => {
  const { t } = useI18n();
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { currentSubscription().then((s) => setOn(Boolean(s))).catch(() => {}); }, []);
  if (!cfg.push_available) return <p className="text-xs text-slate-500" data-testid="push-unavailable">{t("x.push.unavailable")}</p>;
  if (needsHomeScreen()) return <IosGuide />;
  if (!pushSupported()) return <p className="text-xs text-slate-500" data-testid="push-unsupported">{t("x.push.unsupported")}</p>;
  const toggle = async () => {
    setBusy(true);
    try {
      if (on) { await disablePush(); setOn(false); } else {
        const res = await enablePush(cfg.public_key);
        if (res === "granted") { setOn(true); toast.success(t("x.push.enabled")); } else toast.error(t("x.push.denied"));
      }
      onChange();
    } catch (e) { toast.error(t("common.error")); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 p-3">
      <div className="text-sm"><p className="font-medium text-navy">{t("x.push.device")}</p><p className="text-xs text-slate-500" data-testid="push-device-state">{on ? t("x.push.deviceOn") : t("x.push.deviceOff")}</p></div>
      <button type="button" disabled={busy} onClick={toggle} className={on ? btnGhost : btnPrimary} data-testid="push-device-toggle">{on ? t("x.push.disable") : t("x.push.enable")}</button>
    </div>
  );
};

export const NotifySettings = ({ onTest }) => {
  const { t } = useI18n();
  const [cfg, setCfg] = useState(null);
  const load = () => api.get("/notifications/config").then((r) => setCfg(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!cfg) return <div className="h-40" />;
  const save = (prefs) => {
    setCfg((c) => ({ ...c, prefs }));
    api.put("/notifications/prefs", prefs).then(() => toast.success(t("x.push.saved"))).catch(() => toast.error(t("common.error")));
  };
  const p = cfg.prefs;
  const test = async () => {
    try { const { data } = await api.post("/notifications/test"); toast.success(t("x.push.testSent", { n: data.sent })); onTest?.(); window.dispatchEvent(new Event("vavlis:notify")); } catch (e) { toast.error(t("common.error")); }
  };
  return (
    <section className="space-y-4 p-5" data-testid="notify-settings">
      <h2 className="flex items-center gap-2 text-base font-semibold text-navy md:text-lg"><BellRing className="h-5 w-5" />{t("x.push.settings")}</h2>
      <Device cfg={cfg} onChange={load} />
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{t("x.push.cats")}</p>
        <ul className="mt-1 divide-y divide-slate-100">
          {cfg.categories.map((k) => (
            <li key={k} className="flex min-h-12 items-center justify-between gap-3 text-sm text-ink">
              <span>{t(`x.push.cat.${k}`)}</span>
              <Switch checked={p.categories[k] !== false} onCheckedChange={(v) => save({ ...p, categories: { ...p.categories, [k]: v } })} data-testid={`push-cat-${k}`} className="data-[state=checked]:bg-navy" />
            </li>
          ))}
        </ul>
      </div>
      <div className="rounded-xl bg-slate-50 p-3">
        <div className="flex items-center justify-between gap-3 text-sm"><span className="font-medium text-navy">{t("x.push.quiet")}</span>
          <Switch checked={p.quiet_enabled} onCheckedChange={(v) => save({ ...p, quiet_enabled: v })} data-testid="push-quiet-toggle" className="data-[state=checked]:bg-navy" /></div>
        {p.quiet_enabled && <div className="mt-2 grid grid-cols-2 gap-2">
          {["quiet_from", "quiet_to"].map((k) => (
            <label key={k} className="text-xs text-slate-500">{t(k === "quiet_from" ? "x.push.from" : "x.push.to")}
              <select value={p[k]} onChange={(e) => save({ ...p, [k]: e.target.value })} className={`${input} mt-1`} data-testid={`push-${k.replace("_", "-")}`}>
                {HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
              </select></label>
          ))}
        </div>}
      </div>
      <button type="button" onClick={test} className={`${btnGhost} w-full`} data-testid="push-test-button">{t("x.push.test")}</button>
    </section>
  );
};
